import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

interface UploadResult {
  publicUrl: string | null;
  error: Error | null;
}

export async function uploadImageToStorage(file: File): Promise<UploadResult> {
  const maxRetries = 3;
  const baseDelay = 1000;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Only images are allowed.');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 5MB.');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('post-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data } = supabase.storage
        .from('post-images')
        .getPublicUrl(filePath);

      if (!data?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Validate the uploaded image is accessible
      const response = await fetch(data.publicUrl, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error('Uploaded image not accessible');
      }

      return {
        publicUrl: data.publicUrl,
        error: null
      };
    } catch (error) {
      attempt++;
      
      if (attempt === maxRetries) {
        console.error('Error uploading file:', error);
        return {
          publicUrl: null,
          error: error instanceof Error ? error : new Error('Unknown error occurred')
        };
      }

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
    }
  }

  return {
    publicUrl: null,
    error: new Error('Upload failed after retries')
  };
}

/**
 * Upload any media file (image or video) to Supabase Storage.
 * Used by Reels & Stories — accepts video/mp4, video/quicktime, etc.
 */
export async function uploadMediaToStorage(
  file: File,
  options?: { maxSizeMB?: number; bucket?: string }
): Promise<UploadResult> {
  const maxRetries = 3;
  const baseDelay = 1000;
  let attempt = 0;
  const maxSizeMB = options?.maxSizeMB ?? 100;
  const bucket = options?.bucket ?? 'post-images';

  while (attempt < maxRetries) {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type — images or videos
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        throw new Error('Invalid file type. Only images and videos are allowed.');
      }

      // Validate file size
      const maxSize = maxSizeMB * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File size too large. Maximum size is ${maxSizeMB}MB.`);
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      if (!data?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Validate the uploaded file is accessible
      const response = await fetch(data.publicUrl, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error('Uploaded file not accessible');
      }

      return {
        publicUrl: data.publicUrl,
        error: null
      };
    } catch (error) {
      attempt++;
      
      if (attempt === maxRetries) {
        console.error('Error uploading media:', error);
        return {
          publicUrl: null,
          error: error instanceof Error ? error : new Error('Unknown error occurred')
        };
      }

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
    }
  }

  return {
    publicUrl: null,
    error: new Error('Upload failed after retries')
  };
}

export async function deleteImageFromStorage(imageUrl: string): Promise<void> {
  const maxRetries = 3;
  const baseDelay = 1000;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      // Extract file path from URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');
      const filePath = pathParts[pathParts.length - 1];

      if (!filePath) {
        throw new Error('Invalid image URL');
      }

      // Delete file from storage
      const { error } = await supabase.storage
        .from('post-images')
        .remove([`uploads/${filePath}`]);

      if (error) {
        throw error;
      }

      return;
    } catch (error) {
      attempt++;
      
      if (attempt === maxRetries) {
        console.error('Error deleting file:', error);
        throw error;
      }

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
    }
  }
}